import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, photoSrc } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/content";
import { ImageField } from "@/components/admin/ImageField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const toLines = (s) => s.split("\n").map((t) => t.trim()).filter(Boolean);

const field =
  "bg-[#0A0C10] border-white/15 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#FF3B30] rounded-none h-10";
const area = `${field} h-auto`;

const Label = ({ children }) => (
  <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 mb-1.5">{children}</p>
);

const Group = ({ title, children }) => (
  <div className="border border-white/10 bg-[#12151C] p-5 space-y-4">
    <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-400 border-b border-white/10 pb-3">{title}</p>
    {children}
  </div>
);

const toForm = (c) => ({
  ...c,
  hero_badges_text: (c.hero_badges || []).join("\n"),
  marquee_text: (c.marquee_items || []).join("\n"),
  contact_rows_text: (c.contact_rows || []).map((row) => `${row.label} | ${row.value}`).join("\n"),
  chapters: (c.chapters || []).map((ch) => ({ ...ch })),
  packages: (c.packages || []).map((p) => ({ ...p, features_text: (p.features || []).join("\n") })),
  nav_links: (c.nav_links || []).map((l) => ({ ...l })),
});

const ColorField = ({ label, value, onChange, testid }) => (
  <div>
    <Label>{label}</Label>
    <div className="flex items-center gap-3">
      <input
        type="color"
        data-testid={`${testid}-swatch`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-14 shrink-0 cursor-pointer border border-white/15 bg-transparent p-1"
      />
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={field} />
    </div>
  </div>
);

const fmtSaved = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

export const SiteContentTab = () => {
  const [f, setF] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadHistory = () =>
    api.get("/site-content/history").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    api
      .get("/site-content")
      .then((r) => setF(toForm({ ...DEFAULT_CONTENT, ...(r.data || {}) })))
      .catch(() => toast.error("Could not load site content"));
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
    loadHistory();
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setChapter = (i, k, v) =>
    setF((p) => {
      const chapters = [...p.chapters];
      chapters[i] = { ...chapters[i], [k]: v };
      return { ...p, chapters };
    });
  const setPkg = (i, k, v) =>
    setF((p) => {
      const packages = [...p.packages];
      packages[i] = { ...packages[i], [k]: v };
      return { ...p, packages };
    });
  const setNav = (i, k, v) =>
    setF((p) => {
      const nav_links = [...p.nav_links];
      nav_links[i] = { ...nav_links[i], [k]: v };
      return { ...p, nav_links };
    });
  const moveNav = (i, dir) =>
    setF((p) => {
      const arr = [...p.nav_links];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return p;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...p, nav_links: arr };
    });

  const save = async () => {
    setSaving(true);
    const data = {
      hero_kicker: f.hero_kicker,
      hero_line1: f.hero_line1,
      hero_line2: f.hero_line2,
      hero_line3: f.hero_line3,
      hero_sub: f.hero_sub,
      hero_image: f.hero_image,
      hero_badges: toLines(f.hero_badges_text),
      marquee_items: toLines(f.marquee_text),
      about_intro: f.about_intro,
      about_image: f.about_image,
      chapters: f.chapters.map(({ num, title, text }) => ({ num, title, text })),
      services_intro: f.services_intro,
      packages: f.packages.map(({ name, price, tag, features_text }) => ({
        name,
        price,
        tag,
        features: toLines(features_text),
      })),
      contact_intro: f.contact_intro,
      contact_rows: toLines(f.contact_rows_text).map((r) => {
        const [label, ...rest] = r.split("|");
        return { label: (label || "").trim(), value: rest.join("|").trim() };
      }),
      footer_tagline: f.footer_tagline,
      nav_links: f.nav_links.map(({ label, target, testid, visible }) => ({ label, target, testid, visible })),
      color_accent: f.color_accent,
      color_amber: f.color_amber,
      color_bg: f.color_bg,
    };
    try {
      await api.put("/site-content", data);
      toast.success("Site content saved — live on the site immediately.");
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (v) => {
    try {
      const r = await api.post(`/site-content/restore/${v.id}`);
      setF(toForm({ ...DEFAULT_CONTENT, ...r.data }));
      loadHistory();
      toast.success("Version restored and live.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!f) {
    return (
      <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-500 py-10" data-testid="site-content-loading">
        Loading site copy…
      </p>
    );
  }

  return (
    <div data-testid="admin-site-content-tab" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
          Every word on the public site, editable
        </p>
        <button
          data-testid="site-content-save-btn"
          onClick={save}
          disabled={saving}
          className="bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-[11px] font-mono uppercase tracking-[0.18em] px-6 py-3 transition-colors"
        >
          {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      <Group title="Navigation Menu">
        <p className="text-[10px] font-mono text-slate-600">
          Rename, reorder, or hide header links. Sections stay on the page even when hidden.
        </p>
        {f.nav_links.map((l, i) => (
          <div key={l.target} data-testid={`sc-nav-item-${i}`} className="flex items-center gap-3">
            <div className="flex flex-col shrink-0">
              <button
                type="button"
                data-testid={`sc-nav-up-${i}`}
                onClick={() => moveNav(i, -1)}
                disabled={i === 0}
                className="text-slate-500 hover:text-amber-400 disabled:opacity-30 transition-colors"
                aria-label="Move up"
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                data-testid={`sc-nav-down-${i}`}
                onClick={() => moveNav(i, 1)}
                disabled={i === f.nav_links.length - 1}
                className="text-slate-500 hover:text-amber-400 disabled:opacity-30 transition-colors"
                aria-label="Move down"
              >
                <ArrowDown size={14} />
              </button>
            </div>
            <Input
              data-testid={`sc-nav-label-${i}`}
              value={l.label}
              onChange={(e) => setNav(i, "label", e.target.value)}
              className={field}
            />
            <span className="text-[10px] font-mono text-slate-600 shrink-0 hidden sm:inline">{l.target}</span>
            <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 shrink-0">
              <input
                type="checkbox"
                data-testid={`sc-nav-visible-${i}`}
                checked={l.visible}
                onChange={(e) => setNav(i, "visible", e.target.checked)}
                className="accent-[#FF3B30] h-4 w-4"
              />
              Show
            </label>
          </div>
        ))}
      </Group>

      <Group title="Theme Colours">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ColorField label="Accent — buttons, highlights" value={f.color_accent} onChange={(v) => set("color_accent", v)} testid="sc-color-accent" />
          <ColorField label="Amber — labels, tags" value={f.color_amber} onChange={(v) => set("color_amber", v)} testid="sc-color-amber" />
          <ColorField label="Page background" value={f.color_bg} onChange={(v) => set("color_bg", v)} testid="sc-color-bg" />
        </div>
        <p className="text-[10px] font-mono text-slate-600">
          Applies to the public site after save. The admin desk keeps its fixed dark look.
        </p>
      </Group>

      <Group title="Hero">
        <div>
          <Label>Kicker line</Label>
          <Input data-testid="sc-hero-kicker" value={f.hero_kicker} onChange={(e) => set("hero_kicker", e.target.value)} className={field} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Headline line 1 (white)</Label>
            <Input data-testid="sc-hero-line1" value={f.hero_line1} onChange={(e) => set("hero_line1", e.target.value)} className={field} />
          </div>
          <div>
            <Label>Headline line 2 (accent)</Label>
            <Input data-testid="sc-hero-line2" value={f.hero_line2} onChange={(e) => set("hero_line2", e.target.value)} className={field} />
          </div>
          <div>
            <Label>Headline line 3 (outline)</Label>
            <Input data-testid="sc-hero-line3" value={f.hero_line3} onChange={(e) => set("hero_line3", e.target.value)} className={field} />
          </div>
        </div>
        <div>
          <Label>Intro paragraph</Label>
          <Textarea data-testid="sc-hero-sub" rows={3} value={f.hero_sub} onChange={(e) => set("hero_sub", e.target.value)} className={area} />
        </div>
        <ImageField label="Background image — paste a link or pick from your library" value={f.hero_image} onChange={(v) => set("hero_image", v)} photos={photos} testid="sc-hero-image" />
        <div>
          <Label>Floating spec badges — one per line</Label>
          <Textarea data-testid="sc-hero-badges" rows={4} value={f.hero_badges_text} onChange={(e) => set("hero_badges_text", e.target.value)} className={area} />
        </div>
      </Group>

      <Group title="Ticker Marquee">
        <div>
          <Label>Ticker items — one per line</Label>
          <Textarea data-testid="sc-marquee" rows={6} value={f.marquee_text} onChange={(e) => set("marquee_text", e.target.value)} className={area} />
        </div>
      </Group>

      <Group title="Contact">
        <div>
          <Label>Intro paragraph</Label>
          <Textarea data-testid="sc-contact-intro" rows={2} value={f.contact_intro} onChange={(e) => set("contact_intro", e.target.value)} className={area} />
        </div>
        <div>
          <Label>Contact rows — one per line, format: Label | Value</Label>
          <Textarea data-testid="sc-contact-rows" rows={4} value={f.contact_rows_text} onChange={(e) => set("contact_rows_text", e.target.value)} className={area} />
        </div>
      </Group>

      <Group title="Footer">
        <div>
          <Label>Tagline under the logo</Label>
          <Input data-testid="sc-footer-tagline" value={f.footer_tagline} onChange={(e) => set("footer_tagline", e.target.value)} className={field} />
        </div>
      </Group>

      <button
        data-testid="site-content-save-btn-bottom"
        onClick={save}
        disabled={saving}
        className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
      >
        {saving ? "Saving…" : "Save All Changes"}
      </button>
    </div>
  );
};
